package io.horion.cv.components.preview

import androidx.compose.runtime.Composable
import io.horion.cv.api.datasource.cv.OrionCV
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val SIDEBAR_BG   = "#2B74BA"
private const val ACCENT_BLUE  = "#2B74BA"
private const val LIGHT_BLUE   = "#2B74BA"
private const val CITY_COLOR   = "#e07b2a"
private const val TEXT_DARK    = "#1f2937"
private const val WHITE        = "#ffffff"
private const val LIGHT_GRAY   = "#b0bec5"

private const val BG_TEMPLATE  = "/cv-simple-bg.png"

@Composable
fun CvSimplePreview(data: OrionCV) {

    val pi = data.personalInformation

    Div(attrs = {
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("width", "794px")
            property("height", "1123px")
            property("font-family", "Inter, 'Segoe UI', Arial, sans-serif")
            property("font-size", "11px")
            property("color", TEXT_DARK)
            property("box-sizing", "border-box")
            property("overflow", "hidden")
            property("position", "relative")
            property("background-image", "url('$BG_TEMPLATE')")
            property("background-size", "794px 1123px")
            property("background-repeat", "no-repeat")
            property("background-position", "top left")
        }
    }) {
        Div(attrs = {
            style {
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "center")
                property("width", "100%")
                property("max-width", "100%")
                property("min-width", "0")
                property("box-sizing", "border-box")
                property("padding", "32px 20px 36px")
                property("position", "relative")
            }
        }) {
            Div(
                attrs = {
                    style {
                        property("width", "100%")
                        property("max-width", "100%")
                        property("min-width", "0")
                        property("box-sizing", "border-box")
                        property("color", WHITE)
                        property("font-size", "26px")
                        property("font-weight", "700")
                        property("line-height", "1.2")
                        property("letter-spacing", "2px")
                        property("text-transform", "uppercase")
                        property("text-align", "center")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                        property("z-index", "1")
                    }
                }) {
                Text("${pi.firstName} ${pi.lastName}".trim())
            }

            if (pi.bp.isNotBlank()) {
                Div(
                    attrs = {
                        style {
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("color", WHITE)
                            property("font-size", "14px")
                            property("line-height", "1.3")
                            property("letter-spacing", "3px")
                            property("text-transform", "uppercase")
                            property("text-align", "center")
                            property("white-space", "normal")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                            property("margin-top", "6px")
                            property("opacity", "0.9")
                            property("z-index", "1")
                        }
                    }) {
                    Text(pi.bp)
                }
            }
        }

        // ── BODY ───────────────────────────────────────────────────────────────
        Div(attrs = {
            style {
                property("display", "flex")
                property("flex", "1")
                property("width", "100%")
                property("max-width", "100%")
                property("min-width", "0")
                property("box-sizing", "border-box")
                property("margin-top", "-25px")
                property("overflow", "hidden")
            }
        }) {

            // ── SIDEBAR ───────────────────────────────────────────────────────
            Div(attrs = {
                style {
                    property("width", "250px")
                    property("min-height", "1000px")
                    property("color", TEXT_DARK)
                    property("padding", "21px 0 24px 0")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "4px")
                    property("flex-shrink", "0")
                    property("min-width", "250px")
                    property("max-width", "250px")
                    property("flex-shrink", "0")
                    property("box-sizing", "border-box")
                    property("overflow", "hidden")
                }
            }) {

                // CONTACT
                val location = listOfNotNull(
                    pi.city.takeIf { it.isNotBlank() },
                    pi.state.takeIf { it.isNotBlank() },
                    pi.country.takeIf { it.isNotBlank() }
                ).joinToString(", ")
                val hasWebsite = pi.siteWeb?.second?.isNotBlank() == true
                val hasLinks   = pi.links.any { it.second.isNotBlank() }
                val hasContact = pi.email.isNotBlank() || pi.phone.isNotBlank() || pi.address.isNotBlank() || location.isNotBlank() || hasWebsite || hasLinks
                if (hasContact) {
                    SidebarBand("CONTACT")
                    if (pi.email.isNotBlank())   SidebarPlainRow(pi.email)
                    if (pi.phone.isNotBlank())   SidebarPlainRow(pi.phone)
                    if (pi.address.isNotBlank()) SidebarPlainRow(pi.address)
                    else if (location.isNotBlank()) SidebarPlainRow(location)
                    pi.siteWeb?.let { (_, url) -> if (url.isNotBlank()) SidebarLinkRow(url) }
                    pi.links.forEach { (_, url) -> if (url.isNotBlank()) SidebarLinkRow(url) }
                }

                // PERSONAL INFO
                val hasBirth   = pi.birth.isNotBlank()
                val hasNat     = pi.nationality.isNotBlank()
                val hasMarital = pi.maritalStatus.isNotBlank()
                val hasLicense = pi.drivingLicense.isNotBlank()
                if (hasBirth || hasNat || hasMarital || hasLicense) {
                    SidebarBand("PERSONAL INFO")
                    if (hasBirth)   SidebarPlainRow(pi.birth)
                    if (hasNat)     SidebarPlainRow(pi.nationality)
                    if (hasMarital) SidebarPlainRow(pi.maritalStatus)
                    if (hasLicense) SidebarPlainRow(pi.drivingLicense)
                }

                // LANGUAGES
                val validLanguages = data.languages.filter { it.language.name.isNotBlank() }
                if (validLanguages.isNotEmpty()) {
                    SidebarBand("LANGUAGES")
                    validLanguages.forEach { lang ->
                        LangBar(lang.language.name, lang.level.num)
                    }
                }

                // COMPUTER SKILLS
                val validCompetences = data.competences.filter { it.name.isNotBlank() }
                if (validCompetences.isNotEmpty()) {
                    SidebarBand("COMPUTER SKILLS")
                    validCompetences.forEach { comp ->
                        DotSkill(comp.name, (comp.level.num * 5).toInt().coerceIn(0, 5))
                    }
                }

                // SOFTWARE
                val validSoftware = data.software.filter { it.name.isNotBlank() }
                if (validSoftware.isNotEmpty()) {
                    SidebarBand("SOFTWARE")
                    validSoftware.forEach { sw ->
                        DotSkill(sw.name, sw.stars.coerceIn(0, 5))
                    }
                }

                // QUALITIES
                val validQualities = data.qualities.filter { it.isNotBlank() }
                if (validQualities.isNotEmpty()) {
                    SidebarBand("QUALITIES")
                    Div(attrs = {
                        style {
                            property("display", "block")
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("color", TEXT_DARK)
                            property("font-size", "10.5px")
                            property("line-height", "1.6")
                            property("margin-top", "6px")
                            property("padding", "0 20px")
                            property("white-space", "normal")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }
                    }) {
                        Text(validQualities.joinToString(", "))
                    }
                }

                // HOBBIES
                val validHobbies = data.hobbies.filter { it.isNotBlank() }
                if (validHobbies.isNotEmpty()) {
                    SidebarBand("HOBBIES")

                    Div(attrs = {
                        style {
                            property("display", "block")
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("color", TEXT_DARK)
                            property("font-size", "10.5px")
                            property("line-height", "1.6")
                            property("margin-top", "6px")
                            property("padding", "0 20px")
                            property("white-space", "normal")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }
                    }) {
                        Text(validHobbies.joinToString(", "))
                    }
                }
            }

            // ── CONTENU PRINCIPAL ─────────────────────────────────────────────
            Div(attrs = {
                style {
                    property("flex", "1")
                    property("width", "0")
                    property("min-width", "0")
                    property("max-width", "100%")
                    property("overflow", "hidden")
                    property("padding", "35px 25px 24px 20px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "4px")
                    property("box-sizing", "border-box")

                }
            }) {

                // PROFILE
                if (!data.profil.isNullOrBlank()) {
                    MainBand("PROFILE")
                    HtmlBlock(data.profil) {
                        property("font-size", "11px")
                        property("color", "#555555")
                        property("line-height", "1.6")
                        property("margin-top", "3px")
                        property("padding-left", "10px")
                    }
                }

                // DIPLOMAS AND TRAINING
                val validFormations = data.formations.filter {
                    it.label.isNotBlank() || it.school.isNotBlank() || (it.fieldOfStudy?.isNotBlank() == true)
                }
                if (validFormations.isNotEmpty()) {
                    MainBand("DIPLOMAS AND TRAINING")
                    Div(attrs = {
                        style {
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("padding-left", "10px")
                            property("overflow", "hidden")
                        }
                    }) {
                        validFormations.forEachIndexed { index, f ->
                            TimelineEntry(
                                startYear = extractYear(f.started),
                                endYear   = extractYear(f.ended),
                                title     = f.label,
                                line2     = f.fieldOfStudy ?: "",
                                line3     = f.school,
                                bullets   = if (f.description.isNotBlank()) listOf(f.description) else emptyList(),
                                isExp     = false,
                                isLast    = index == validFormations.lastIndex
                            )
                        }
                    }
                }

                // PROFESSIONAL EXPERIENCES
                val validExperiences = data.experiences.filter {
                    it.post.isNotBlank() || it.enterprise.isNotBlank()
                }
                if (validExperiences.isNotEmpty()) {
                    MainBand("PROFESSIONAL EXPERIENCES")
                    Div(attrs = {
                        style {
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("padding-left", "10px")
                            property("overflow", "hidden")
                        }
                    }) {
                        validExperiences.forEachIndexed { index, exp ->
                            val expTitle = when {
                                exp.post.isNotBlank() && exp.enterprise.isNotBlank() -> "${exp.post} (${exp.enterprise})"
                                exp.post.isNotBlank() -> exp.post
                                else -> exp.enterprise
                            }
                            TimelineEntry(
                                startYear = extractYear(exp.started),
                                endYear   = extractYear(exp.ended),
                                title     = expTitle,
                                line2     = exp.city,
                                line3     = "",
                                bullets   = exp.description,
                                isExp     = true,
                                isLast    = index == validExperiences.lastIndex
                            )
                        }
                    }
                }

                // STAGES
                val validStages = data.stages.filter {
                    it.post.isNotBlank() || it.enterprise.isNotBlank()
                }
                if (validStages.isNotEmpty()) {
                    MainBand("INTERNSHIPS")
                    Div(attrs = {
                        style {
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("padding-left", "10px")
                            property("overflow", "hidden")
                        }
                    }) {
                        validStages.forEachIndexed { index, stage ->
                            val stageTitle = when {
                                stage.post.isNotBlank() && stage.enterprise.isNotBlank() -> "${stage.post} (${stage.enterprise})"
                                stage.post.isNotBlank() -> stage.post
                                else -> stage.enterprise
                            }
                            TimelineEntry(
                                startYear = extractYear(stage.started),
                                endYear   = extractYear(stage.ended),
                                title     = stageTitle,
                                line2     = stage.city,
                                line3     = "",
                                bullets   = if (stage.description.isNotBlank()) listOf(stage.description) else emptyList(),
                                isExp     = true,
                                isLast    = index == validStages.lastIndex
                            )
                        }
                    }
                }

                // COURSES
                val validLearns = data.learns.filter { it.label.isNotBlank() }
                if (validLearns.isNotEmpty()) {
                    MainBand("COURSES")
                    Div(attrs = {
                        style {
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("padding-left", "10px")
                            property("overflow", "hidden")
                        }
                    }) {
                        validLearns.forEachIndexed { index, learn ->
                            TimelineEntry(
                                startYear = extractYear(learn.started),
                                endYear   = extractYear(learn.ended),
                                title     = learn.label,
                                line2     = "",
                                line3     = "",
                                bullets   = if (learn.description.isNotBlank()) listOf(learn.description) else emptyList(),
                                isExp     = false,
                                isLast    = index == validLearns.lastIndex
                            )
                        }
                    }
                }

                // EXTRACURRICULAR ACTIVITIES
                val validActivities = data.extracurricularActivities.filter {
                    it.name.isNotBlank() || it.enterprise.isNotBlank()
                }
                if (validActivities.isNotEmpty()) {
                    MainBand("EXTRACURRICULAR ACTIVITIES")
                    Div(attrs = {
                        style {
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("padding-left", "10px")
                            property("overflow", "hidden")
                        }
                    }) {
                        validActivities.forEachIndexed { index, activity ->
                            val actTitle = when {
                                activity.name.isNotBlank() && activity.enterprise.isNotBlank() -> "${activity.name} (${activity.enterprise})"
                                activity.name.isNotBlank() -> activity.name
                                else -> activity.enterprise
                            }
                            TimelineEntry(
                                startYear = extractYear(activity.started),
                                endYear   = extractYear(activity.ended),
                                title     = actTitle,
                                line2     = activity.city,
                                line3     = "",
                                bullets   = if (activity.description.isNotBlank()) listOf(activity.description) else emptyList(),
                                isExp     = true,
                                isLast    = index == validActivities.lastIndex
                            )
                        }
                    }
                }

                // CERTIFICATES
                val validCertificates = data.certificates.filter { it.name.isNotBlank() }
                if (validCertificates.isNotEmpty()) {
                    MainBand("CERTIFICATES")
                    Div(attrs = {
                        style {
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                            property("padding-left", "10px")
                            property("overflow", "hidden")
                        }
                    }) {
                        validCertificates.forEachIndexed { index, cert ->
                            TimelineEntry(
                                startYear = extractYear(cert.date),
                                endYear   = "",
                                title     = cert.name,
                                line2     = "",
                                line3     = "",
                                bullets   = if (cert.description.isNotBlank()) listOf(cert.description) else emptyList(),
                                isExp     = false,
                                isLast    = index == validCertificates.lastIndex
                            )
                        }
                    }
                }

                // ACHIEVEMENTS
                val validAchievements = data.achievements.filter { it.isNotBlank() }
                if (validAchievements.isNotEmpty()) {
                    MainBand("ACHIEVEMENTS")
                    Div(attrs = { style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "3px")
                        property("margin-top", "6px")
                    }}) {
                        validAchievements.forEach { item ->
                            Div(attrs = { style { property("display", "flex"); property("gap", "5px"); property("font-size", "10px"); property("color", TEXT_DARK) } }) {
                                Span(attrs = { style { property("flex-shrink", "0") } }) { Text("•") }
                                HtmlInline(item)
                            }
                        }
                    }
                }

                // ── RUBRIQUES PERSONNALISÉES ──────────────────────────────────────────────
                val validPersonalizedSections =
                    data.personalizedSections.filter { section ->
                        section.resume.isNotBlank() ||
                                section.started.isNotBlank() ||
                                section.ended.isNotBlank() ||
                                section.description.isNotBlank()
                    }

                if (validPersonalizedSections.isNotEmpty()) {
                    validPersonalizedSections.forEachIndexed { index, section ->
                        PersonalizedSectionPreview(
                            section = section,
                            isLast = index == validPersonalizedSections.lastIndex
                        )
                    }
                }

                // REFERENCES
                val validReferences = data.references.filter { it.name.isNotBlank() || it.enterprise.isNotBlank() }
                if (validReferences.isNotEmpty()) {
                    MainBand("REFERENCES")
                    Div(attrs = {
                        style {
                            property("display", "flex")
                            property("flex-direction", "column")
                            property("gap", "8px")
                            property("margin-top", "6px")
                            property("width", "100%")
                            property("max-width", "100%")
                            property("min-width", "0")
                            property("box-sizing", "border-box")
                        }
                    }) {
                        validReferences.forEach { ref ->
                            ReferenceCard(ref)
                        }
                    }
                }

                // SIGNATURE
                data.signature?.let { sig ->
                    if (
                        sig.city.isNotBlank() ||
                        sig.date.isNotBlank() ||
                        sig.image.isNotBlank()
                    ) {
                        Div(attrs = {
                            style {
                                property("display", "flex")
                                property("justify-content", "flex-end")
                                property("width", "100%")
                                property("max-width", "100%")
                                property("min-width", "0")
                                property("box-sizing", "border-box")
                                property("margin-top", "10px")
                                property("overflow", "hidden")
                            }
                        }) {
                            Div(attrs = {
                                style {
                                    property("width", "100%")
                                    property("max-width", "100%")
                                    property("min-width", "0")
                                    property("box-sizing", "border-box")
                                    property("text-align", "right")
                                    property("overflow-wrap", "anywhere")
                                    property("word-break", "break-word")
                                }
                            }) {
                                val line = listOfNotNull(
                                    sig.city.takeIf { it.isNotBlank() },
                                    sig.date.takeIf { it.isNotBlank() }
                                )

                                if (line.isNotEmpty()) {
                                    Div(attrs = {
                                        style {
                                            property("max-width", "100%")
                                            property("font-size", "10px")
                                            property("line-height", "1.3")
                                            property("color", "#666666")
                                            property("white-space", "normal")
                                            property("overflow-wrap", "anywhere")
                                            property("word-break", "break-word")
                                        }
                                    }) {
                                        Text(line.joinToString(", "))
                                    }
                                }

                                if (sig.image.isNotBlank()) {
                                    Img(
                                        src = sig.image,
                                        attrs = {
                                            style {
                                                property("display", "inline-block")
                                                property("max-width", "100%")
                                                property("width", "auto")
                                                property("height", "50px")
                                                property("object-fit", "contain")
                                                property("margin-top", "4px")
                                            }
                                        }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        // ── BAS DE PAGE ────────────────────────────────────────────────────────
        Div(attrs = { style {
            property("height", "45px")
            property("flex-shrink", "0")
        }})
    }
}

// ── Sidebar : bandeau titre section ───────────────────────────────────────────

@Composable
private fun SidebarBand(title: String) {
    Div(attrs = { style {
        property("background-color", ACCENT_BLUE)
        property("color", WHITE)
        property("font-size", "11px")
        property("font-weight", "700")
        property("letter-spacing", "1px")
        property("padding", "6px 12px")
        property("border-radius", "0px")
        property("width", "calc(100% - 15px)")
        property("margin-top", "14px")
        property("margin-bottom", "8px")
        property("box-shadow", "5px 5px 9px rgba(0,0,0,0.25)")
    }}) { Text(title) }
}

// ── Sidebar : ligne texte simple (contact sans icône) ─────────────────────────

@Composable
private fun SidebarPlainRow(value: String) {
    Div(attrs = { style {
        property("width", "100%")
        property("box-sizing", "border-box")
        property("color", "#4b5563")
        property("font-size", "10.5px")
        property("margin-bottom", "4px")
        property("padding", "0 20px")
        property("white-space", "normal")
        property("overflow-wrap", "anywhere")
        property("word-break", "break-word")
    }}) { Text(value) }
}

// ── Sidebar : ligne lien cliquable (site web, réseaux) ────────────────────────

@Composable
private fun SidebarLinkRow(url: String) {
    Div(attrs = {
        style {
            property("width", "100%")
            property("box-sizing", "border-box")
            property("font-size", "10.5px")
            property("margin-bottom", "4px")
            property("padding", "0 20px")
            property("overflow-wrap", "anywhere")
            property("word-break", "break-word")
        }
    }) {
        A(href = url, attrs = {
            attr("target", "_blank")
            attr("rel", "noopener noreferrer")

            style {
                property("color", ACCENT_BLUE)
                property("text-decoration", "underline")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            Text(url)
        }
    }
}

// ── Sidebar : barre de langue ─────────────────────────────────────────────────

@Composable
private fun LangBar(name: String, level: Float) {
    Div(attrs = {
        style {
            property("display", "flex")
            property("align-items", "flex-start")
            property("gap", "6px")
            property("width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("margin-bottom", "6px")
            property("padding", "0 20px")
    }}) {
        Span(attrs = {
            style {
                property("flex", "1")
                property("min-width", "0")
                property("max-width", "100%")
                property("color", TEXT_DARK)
                property("font-size", "10.5px")
                property("font-weight", "600")
                property("line-height", "1.3")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            Text(name)
        }
        Div(attrs = { style {
            property("width", "130px")
            property("height", "14px")
            property("background", LIGHT_GRAY)
            property("border-radius", "0px")
            property("overflow", "hidden")
        }}) {
            Div(attrs = { style {
                property("height", "100%")
                property("width", "${(level * 100).toInt().coerceIn(0, 100)}%")
                property("background", SIDEBAR_BG)
            }})
        }
    }
}

// ── Sidebar : compétence avec 5 carrés ────────────────────────────────────────

@Composable
private fun DotSkill(name: String, filled: Int) {
    Div(attrs = {
        style {
            property("display", "flex")
            property("align-items", "flex-start")
            property("gap", "6px")
            property("width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("margin-bottom", "6px")
            property("padding", "0 20px")
        }
    }) {
        Span(attrs = {
            style {
                property("flex", "1")
                property("min-width", "0")
                property("max-width", "100%")
                property("color", TEXT_DARK)
                property("font-size", "10.5px")
                property("font-weight", "600")
                property("line-height", "1.3")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            Text(name)
        }

        Div(attrs = {
            style {
                property("display", "flex")
                property("gap", "3px")
                property("flex-shrink", "0")
                property("padding-top", "1px")
            }
        }) {
            repeat(5) { i ->
                Div(attrs = {
                    style {
                        property("width", "13px")
                        property("height", "13px")
                        property("flex-shrink", "0")
                        property(
                            "background-color",
                            if (i < filled) SIDEBAR_BG else LIGHT_GRAY
                        )
                    }
                })
            }
        }
    }
}

// ── Contenu principal : bandeau section (pilule arrondie) ───────────────────

@Composable
private fun MainBand(title: String) {
    Div(attrs = { style {
        property("display", "flex")
        property("align-items", "center")
        property("margin-bottom", "4px")
        property("width", "100%")
    }}) {
        // Pilule pleine, dimensionnée au texte du titre
        Div(attrs = { style {
            property("position", "relative")
            property("z-index", "1")
            property("background-color", ACCENT_BLUE)
            property("color", WHITE)
            property("font-size", "11px")
            property("font-weight", "400")
            property("letter-spacing", "0.5px")
            property("padding", "5px 40px 5px 18px")
            property("border-radius", "999px")
            property("text-transform", "uppercase")
            property("white-space", "nowrap")
        }}) { Text(title) }

        Div(attrs = { style {
            property("height", "28px")
            property("width", "25px")
            property("margin-left", "-15px")
            property("background", "rgba(43,116,186,0.12)")
            property("border-radius", "999px")
        }})
    }
}

// ── Carte référence ────────────────────────────────────────────────────────────

@Composable
private fun ReferenceCard(
    ref: io.horion.cv.api.datasource.cv.Reference
) {
    Div(attrs = {
        style {
            property("display", "block")
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("overflow", "hidden")
            property("border-bottom", "1px solid #e5e7eb")
            property("padding-bottom", "6px")
        }
    }) {
        Div(attrs = {
            style {
                property("width", "100%")
                property("max-width", "100%")
                property("min-width", "0")
                property("box-sizing", "border-box")
                property("font-weight", "700")
                property("font-size", "11px")
                property("line-height", "1.3")
                property("color", SIDEBAR_BG)
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            Text(ref.name)
        }

        val subtitleParts = listOfNotNull(
            ref.enterprise.takeIf { it.isNotBlank() },
            ref.city.takeIf { it.isNotBlank() }
        )

        if (subtitleParts.isNotEmpty()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("box-sizing", "border-box")
                    property("font-size", "10px")
                    property("line-height", "1.3")
                    property("color", CITY_COLOR)
                    property("margin-top", "2px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(subtitleParts.joinToString(" · "))
            }
        }

        val contactParts = listOfNotNull(
            ref.phone.takeIf { it.isNotBlank() },
            ref.email.takeIf { it.isNotBlank() }
        )

        if (contactParts.isNotEmpty()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("box-sizing", "border-box")
                    property("font-size", "10px")
                    property("line-height", "1.3")
                    property("color", TEXT_DARK)
                    property("margin-top", "2px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(contactParts.joinToString(" • "))
            }
        }
    }
}

// ── Ligne timeline (diplômes + expériences + stages + cours + activités + certificats) ──

@Composable
private fun TimelineEntry(
    startYear: String,
    endYear:   String,
    title:     String,
    line2:     String,   // filière/enterprise OU ville
    line3:     String,   // école (diplôme uniquement)
    bullets:   List<String>,
    isExp:     Boolean,
    isLast:    Boolean = false
) {
    Div(attrs = { style {
        property("display", "flex")
        property("margin-bottom", "8px")
    }}) {

        // Années
        Div(attrs = { style {
            property("width", "34px")
            property("margin-right", "15px")
            property("flex-shrink", "0")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-end")
            property("gap", "1px")
        }}) {
            Span(attrs = { style {
                property("font-size", "10px")
                property("font-weight", "700")
                property("color", SIDEBAR_BG)
            }}) { Text(startYear) }
            if (endYear.isNotBlank() && endYear != startYear) {
                Span(attrs = { style {
                    property("font-size", "10px")
                    property("font-weight", "700")
                    property("color", SIDEBAR_BG)
                }}) { Text(endYear) }
            }
        }

        Div(attrs = { style {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("flex-shrink", "0")
            property("margin-right", "4px")
            property("position", "relative")
            property("width", "11px")
        }}) {
            Div(attrs = { style {
                property("width", "11px")
                property("height", "11px")
                property("border-radius", "50%")
                property("background-color", ACCENT_BLUE)
                property("flex-shrink", "0")
                property("margin-top", "3px")
                property("position", "relative")
                property("z-index", "1")
            }})
            if (!isLast) {
                Div(attrs = { style {
                    property("position", "absolute")
                    property("top", "15px")
                    property("bottom", "-8px")
                    property("left", "5px")
                    property("width", "1px")
                    property("background", LIGHT_BLUE)
                }})
            }
        }

        // Texte
        Div(
            attrs = {
                style {
                    property("flex", "1")
                    property("min-width", "0")
                    property("max-width", "100%")
                    property("padding-bottom", "4px")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
        }}) {
            Div(attrs = { style {
                property("font-size", "11px")
                property("font-weight", "700")
                property("color", SIDEBAR_BG)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.3px")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
                property("white-space", "normal")
            }}) { Text(title) }

            if (line2.isNotBlank()) {
                Div(attrs = { style {
                    property("font-size", "10px")
                    property("margin-top", "1px")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("white-space", "normal")
                    if (isExp) {
                        property("color", CITY_COLOR)
                        property("font-weight", "600")
                    } else {
                        property("color", "#666666")
                    }
                }}) { Text(line2) }
            }

            if (!isExp && line3.isNotBlank()) {
                Div(attrs = { style {
                    property("font-size", "10px")
                    property("color", "#666666")
                    property("margin-top", "1px")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("white-space", "normal")
                }}) { Text(line3) }
            }

            if (bullets.isNotEmpty()) {
                Div(attrs = { style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "2px")
                    property("margin-top", "4px")
                }}) {
                    bullets.forEach { b ->
                        Div(attrs = { style {
                            property("display", "flex")
                            property("gap", "5px")
                            property("font-size", "10px")
                            property("color", "#555555")
                            property("min-width", "0")
                            property("max-width", "100%")
                        }}) {
                            Span(attrs = { style {
                                property("flex-shrink", "0")
                                property("margin-top", "1px")
                            }}) { Text("•") }
                            HtmlInline(b)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PersonalizedSectionPreview(
    section: io.horion.cv.api.datasource.cv.Section,
    isLast: Boolean
) {
    MainBand(
        section.title.ifBlank {
            "RUBRIQUE PERSONNALISÉE"
        }
    )

    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("padding-left", "10px")
            property("margin-bottom", "8px")
            property("font-size", "10px")
            property("line-height", "1.5")
            property("color", "#555555")
            property("white-space", "normal")
            property("overflow-wrap", "anywhere")
            property("word-break", "break-word")
        }
    }) {
        if (section.resume.isNotBlank()) {
            Div(attrs = {
                style {
                    property("font-size", "11px")
                    property("font-weight", "700")
                    property("color", SIDEBAR_BG)
                    property("margin-bottom", "3px")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(section.resume)
            }
        }

        if (section.started.isNotBlank() || section.ended.isNotBlank()) {
            Div(attrs = {
                style {
                    property("font-size", "10px")
                    property("color", CITY_COLOR)
                    property("margin-bottom", "3px")
                }
            }) {
                Text(
                    listOf(
                        section.started,
                        section.ended
                    )
                        .filter { it.isNotBlank() }
                        .joinToString(" - ")
                )
            }
        }

        if (section.description.isNotBlank()) {
            HtmlBlock(section.description) {
                property("font-size", "10px")
                property("line-height", "1.5")
                property("color", "#555555")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }
    }
}